import React, { useCallback, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button, Tabs, Spin, message, Tag, Tooltip, Input, DatePicker, Select, Modal, Popconfirm } from 'antd';
import { EditOutlined, SaveOutlined, EyeOutlined, FileImageOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../api/client';
import { listOrders } from '../api/serviceOrders';

interface DocumentParty {
  role: string;
  name: string;
  person_name?: string;
  person_title?: string;
  authority_basis?: string;
  is_our?: boolean;
  has_signature?: boolean;
  has_stamp?: boolean;
}

interface DocumentKeyTerm {
  parameter: string;
  value: string;
}

interface DocumentDetail {
  id: number;
  type_id: number | null;
  subtype_id: number | null;
  number: string | null;
  date: string | null;
  title: string | null;
  legal_entity_ids: string[];
  ai_extracted_data: {
    document_type?: string;
    document_subtype?: string;
    number?: string;
    date?: string;
    place?: string;
    subject?: string;
    parties?: DocumentParty[];
    key_terms?: DocumentKeyTerm[];
  };
  manual_edits: Record<string, boolean>;
  status: string;
  created_at: string;
  updated_at: string;
  doc_type: { name: string } | null;
  doc_subtype: { name: string } | null;
  pages: { id: number; page_number: number; filename: string }[];
  document_id: string | null;
  flow_document: {
    id: string;
    doc_number: string | null;
    doc_type: string;
    status: string;
    service_order_id: number | null;
  } | null;
}

const STATUS_LABELS: Record<string, string> = {
  draft: 'Черновик',
  processed: 'Обработан',
  archived: 'Архив',
};

const ENTITY_LABELS: Record<string, string> = {
  autoregium: 'АвтоРегиум',
  fomin: 'Фомин',
};

const DocumentDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [doc, setDoc] = useState<DocumentDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [previewPageId, setPreviewPageId] = useState<number | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [editForm, setEditForm] = useState<Partial<DocumentDetail>>({});
  const [types, setTypes] = useState<{ id: number; name: string }[]>([]);
  const [orderOptions, setOrderOptions] = useState<{ value: number; label: string }[]>([]);
  const [linkLoading, setLinkLoading] = useState(false);

  const searchOrders = useCallback((q: string) => {
    listOrders({ search: q || undefined })
      .then(r => setOrderOptions((r.items || []).map(o => ({
        value: o.id,
        label: `ЗН № ${o.number || o.external_1c_number || o.id}${o.vehicle ? ' · ' + o.vehicle : ''}`,
      }))))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (doc && !doc.flow_document) searchOrders('');
  }, [doc, searchOrders]);

  const handleLink = useCallback((soId: number) => {
    setLinkLoading(true);
    api.post(`/documents/${id}/link`, { service_order_id: soId })
      .then(r => { setDoc(r.data); message.success('Документ привязан к заказ-наряду'); })
      .catch(() => message.error('Не удалось привязать'))
      .finally(() => setLinkLoading(false));
  }, [id]);

  const handleUnlink = useCallback(() => {
    api.post(`/documents/${id}/link`, {})
      .then(r => { setDoc(r.data); message.success('Связь убрана'); })
      .catch(() => message.error('Не удалось отвязать'));
  }, [id]);

  const load = useCallback(() => {
    if (!id) return;
    setLoading(true);
    api.get(`/documents/${id}`)
      .then(r => {
        setDoc(r.data);
        setEditForm(r.data);
      })
      .catch(() => message.error('Ошибка загрузки документа'))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    load();
    api.get('/documents/types').then(r => setTypes(r.data || [])).catch(() => {});
  }, [load]);

  const openPreview = useCallback((pageId: number) => {
    setPreviewPageId(pageId);
    setPreviewLoading(true);
    api.get(`/documents/pages/${pageId}/file`, { responseType: 'blob' })
      .then(r => {
        const url = URL.createObjectURL(r.data);
        setPreviewUrl(url);
      })
      .catch(() => message.error('Не удалось загрузить страницу'))
      .finally(() => setPreviewLoading(false));
  }, []);

  const closePreview = useCallback(() => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setPreviewPageId(null);
  }, [previewUrl]);

  const handleSave = useCallback(() => {
    if (!id || !editForm) return;
    api.put(`/documents/${id}`, editForm)
      .then(() => {
        message.success('Сохранено');
        setEditing(false);
        load();
      })
      .catch(() => message.error('Ошибка сохранения'));
  }, [id, editForm, load]);

  const renderField = useCallback((
    label: string,
    value: React.ReactNode,
    fieldKey: string,
    editNode?: React.ReactNode
  ) => {
    const isManual = doc?.manual_edits?.[fieldKey];
    return (
      <div
        key={fieldKey}
        style={{
          marginBottom: 12,
          padding: '8px 12px',
          borderRadius: 6,
          background: isManual ? '#fffbe6' : 'transparent',
          transition: 'background 0.2s',
        }}
        title={isManual ? 'Поле отредактировано вручную' : undefined}
      >
        <div style={{ fontSize: 12, color: '#888', marginBottom: 2 }}>{label}</div>
        <div style={{ fontSize: 15 }}>
          {editing && editNode ? (
            editNode
          ) : (
            <Tooltip title={isManual ? 'Отредактировано вручную' : ''}>
              <span>{value || '—'}</span>
            </Tooltip>
          )}
        </div>
      </div>
    );
  }, [doc, editing]);

  if (loading || !doc) {
    return (
      <div style={{ textAlign: 'center', padding: 60 }}>
        <Spin size="large" />
      </div>
    );
  }

  const ai = doc.ai_extracted_data || {};

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <h2 style={{ margin: 0 }}>{doc.title || 'Документ без названия'}</h2>
          <div style={{ fontSize: 13, color: '#888', marginTop: 4 }}>
            <Tag>{STATUS_LABELS[doc.status] || doc.status}</Tag>
            {doc.legal_entity_ids.map(e => (
              <Tag key={e}>{ENTITY_LABELS[e] || e}</Tag>
            ))}
            <span style={{ marginLeft: 8 }}>
              {new Date(doc.created_at).toLocaleString('ru-RU')}
            </span>
          </div>
          <div style={{ marginTop: 8 }}>
            {doc.flow_document ? (
              <span>
                <Tag color="geekblue">
                  Связано: {doc.flow_document.doc_type === 'service_order' ? 'Заказ-наряд' : doc.flow_document.doc_type}
                  {doc.flow_document.doc_number ? ` № ${doc.flow_document.doc_number}` : ''}
                </Tag>
                {doc.flow_document.service_order_id && (
                  <Link to={`/service-orders/${doc.flow_document.service_order_id}`}>Открыть ЗН</Link>
                )}
                <Popconfirm title="Отвязать документ от заказ-наряда?" onConfirm={handleUnlink} okText="Да" cancelText="Нет">
                  <Button type="link" size="small">Отвязать</Button>
                </Popconfirm>
              </span>
            ) : (
              <Select
                showSearch
                placeholder="Привязать к заказ-наряду…"
                style={{ width: 340 }}
                filterOption={false}
                onSearch={searchOrders}
                onChange={handleLink}
                options={orderOptions}
                loading={linkLoading}
                allowClear
              />
            )}
          </div>
        </div>
        <div>
          {editing ? (
            <Button type="primary" icon={<SaveOutlined />} onClick={handleSave} style={{ marginRight: 8 }}>
              Сохранить
            </Button>
          ) : (
            <Button icon={<EditOutlined />} onClick={() => setEditing(true)} style={{ marginRight: 8 }}>
              Редактировать
            </Button>
          )}
        </div>
      </div>

      <Tabs
        items={[
          {
            key: 'data',
            label: 'Данные',
            children: (
              <div style={{ maxWidth: 800 }}>
                {renderField('Тип документа', doc.doc_type?.name || ai.document_type, 'type_id',
                  <Select
                    style={{ width: 300 }}
                    value={editForm.type_id}
                    onChange={v => setEditForm({ ...editForm, type_id: v })}
                    options={types.map(t => ({ value: t.id, label: t.name }))}
                    allowClear
                  />
                )}
                {renderField('Номер', doc.number || ai.number, 'number',
                  <Input
                    value={editForm.number || ''}
                    onChange={e => setEditForm({ ...editForm, number: e.target.value })}
                    style={{ width: 300 }}
                  />
                )}
                {renderField('Дата', doc.date || ai.date, 'date',
                  <DatePicker
                    value={editForm.date ? dayjs(editForm.date) : null}
                    onChange={d => setEditForm({ ...editForm, date: d ? d.format('YYYY-MM-DD') : null })}
                    format="DD.MM.YYYY"
                  />
                )}
                {renderField('Место', ai.place, 'place',
                  <Input
                    value={(editForm as any).place || ''}
                    onChange={e => setEditForm({ ...editForm, ai_extracted_data: { ...ai, place: e.target.value } })}
                    style={{ width: 300 }}
                  />
                )}
                {renderField('Предмет', ai.subject, 'subject',
                  <Input.TextArea
                    value={(editForm as any).subject || ''}
                    onChange={e => setEditForm({ ...editForm, ai_extracted_data: { ...ai, subject: e.target.value } })}
                    rows={2}
                    style={{ width: 300 }}
                  />
                )}

                <div style={{ fontSize: 12, color: '#888', margin: '16px 0 8px' }}>Стороны</div>
                {(ai.parties || []).map((party: DocumentParty, idx: number) => (
                  <div key={idx} style={{ marginBottom: 12, padding: 12, border: '1px solid #eee', borderRadius: 8 }}>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>{party.role} {party.is_our && <Tag color="blue">наше</Tag>}</div>
                    <div>{party.name}</div>
                    {party.person_name && (
                      <div style={{ fontSize: 13, color: '#666' }}>
                        {party.person_title || ''} {party.person_name}
                        {party.authority_basis && <span> ({party.authority_basis})</span>}
                      </div>
                    )}
                    <div style={{ marginTop: 4 }}>
                      {party.has_signature ? <Tag color="success">Подпись ✓</Tag> : <Tag color="default">Подпись ✗</Tag>}
                      {party.has_stamp ? <Tag color="success">Печать ✓</Tag> : <Tag color="default">Печать ✗</Tag>}
                    </div>
                  </div>
                ))}

                <div style={{ fontSize: 12, color: '#888', margin: '16px 0 8px' }}>Ключевые условия</div>
                {(ai.key_terms || []).map((term: DocumentKeyTerm, idx: number) => (
                  <div key={idx} style={{ display: 'flex', marginBottom: 4, fontSize: 14 }}>
                    <div style={{ width: 200, color: '#666' }}>{term.parameter}</div>
                    <div>{term.value}</div>
                  </div>
                ))}
              </div>
            ),
          },
          {
            key: 'pages',
            label: `Страницы (${doc.pages.length})`,
            children: (
              <div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                  {doc.pages.map(page => (
                    <div
                      key={page.id}
                      style={{
                        width: 160,
                        cursor: 'pointer',
                        border: '1px solid #d9d9d9',
                        borderRadius: 8,
                        overflow: 'hidden',
                        textAlign: 'center',
                      }}
                      onClick={() => openPreview(page.id)}
                    >
                      <div style={{ padding: '20px 0', background: '#f5f5f5' }}>
                        <FileImageOutlined style={{ fontSize: 32, color: '#aaa' }} />
                      </div>
                      <div style={{ padding: 8, fontSize: 13 }}>
                        Стр. {page.page_number}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ),
          },
        ]}
      />

      <Modal
        open={!!previewPageId}
        onCancel={closePreview}
        footer={null}
        title="Просмотр страницы"
        width="95vw"
        centered
      >
        {previewLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spin size="large" />
          </div>
        ) : previewUrl ? (
          <img src={previewUrl} alt="Page" style={{ width: '100%', borderRadius: 4 }} />
        ) : null}
      </Modal>
    </div>
  );
};

export default DocumentDetailPage;
